<template>
  <v-container fluid class="fill-height pa-0" @touchmove.prevent>
    <transition name="fade" mode="out-in">

      <v-container fluid class="fill-height" key="intro" v-if="step === 'intro' || step === 'language'">
        <v-row align="center" justify="center">
          <v-col class="text-center pa-0" cols="12">
            <RedCircle size="large" />
          </v-col>
          <transition name="fade" mode="out-in">
            <v-col cols="12" class="pa-0 text-center preloader-tab" v-if="step === 'intro'" key="intro-sub">
              <h4 class="mt-8 text-uppercase">The <strong>covid-19</strong> <br class="d-block d-md-none">visual project</h4>
            </v-col>
            <v-col cols="12" class="pa-0 text-center preloader-tab" v-if="step === 'language'" key="lang-sub">
              <h4 class="mt-8 text-uppercase">Choose ‍‌‍‍a language</h4>
              <v-row justify="center" class="mt-3">
                <v-col cols="3" md="1">
                  <v-btn @click="onLanguageSelected('en')" class="language-btn" text>EN</v-btn>
                </v-col>
                <v-col cols="3" md="1">
                  <v-btn @click="onLanguageSelected('it')" class="language-btn" text>IT</v-btn>
                </v-col>
              </v-row>
            </v-col>
          </transition>
        </v-row>
        <transition name="fade">
          <v-footer absolute v-if="step === 'intro'">
            <v-col>
              <PreloaderIntroBar :percentage="percentage"/>
            </v-col>
          </v-footer>
        </transition>
      </v-container>

      <v-row class="fill-height" align="end" v-else-if="$root.siteSettings"  key="explore">
        <v-col cols="12" class="pa-0 background-image" :style="{height: `${viewPort.height}px`}">
          <seo-v-img :src="'/media/' + randomImageCredit.image" :height="($vuetify.breakpoint.mdAndUp) ? '100%' : '50%'" />

          <div class="credit">
            <span>{{randomImageCredit.credit}}</span>
          </div>
        </v-col>
        <v-col cols="10" md="6" class="info pl-12 ml-md-12 pr-0 pt-4 pb-12 mb-md-8" style="z-index: 2;">
          <h3 class="text-uppercase"><div class="circle-logo"></div>The <strong>Covid-19</strong> Visual Project</h3>
          <h4 class="text-uppercase color-gray underline">A Time of Distance</h4>
          <v-row>
            <v-col md="8" class="ma-0 py-0 pt-6">
              <h5>{{$root.siteSettings.introPageDescription}}</h5>
            </v-col>
          </v-row>
          <MarkdownBlock :inline="true" :linkify="true" tag="p" class="mt-4" :text="$root.siteSettings.introPageCredits" />
          <TextCta @click="onExploreClick" :copy="$root.siteSettings.genericExploreText" />
        </v-col>
      </v-row>

      <v-row class="fill-height ma-0" align="center" justify="center" v-else>
        <v-progress-circular indeterminate color="red" />
      </v-row>

    </transition>
  </v-container>
</template>

<script>
import TextCta from '@/atoms/TextCta'
import RedCircle from '@/atoms/RedCircle'
import PreloaderIntroBar from '@/molecules/PreloaderIntroBar'
import listChapter from '@/graphql/ListChapter.gql'
import ListSiteSettings from '@/graphql/ListSiteSettings.gql'
import anime from 'animejs'
import { MarkdownBlock, Viewport } from '@monogrid/vue-lib'

const INTRO_TIMEOUT = 3000

export default {
  name: 'PreloaderIntro',

  mixins: [Viewport],

  components: {
    TextCta,
    RedCircle,
    MarkdownBlock,
    PreloaderIntroBar
  },

  data () {
    return {
      step: 'intro',
      percentage: 0,
      selectedLanguage: null,
      randomImageCredit: null
    }
  },

  apollo: {
    ListSiteSettings: {
      query: ListSiteSettings,
      variables () {
        return {
          status: 'PUBLISHED'
        }
      },
      result (res) {
        this.$root.allSiteSettings = {}
        const sarray = res.data.ListSiteSettings.items
        for (let index = 0; index < sarray.length; index++) {
          const element = sarray[index]
          this.$root.allSiteSettings[element.language] = element
        }

        if (this.$root.platform.prerenderer && this.step === 'explore') {
          // skip intros and emit loading complete
          this.$emit('loading:complete')
        }
      }
    },
    ListChapter: {
      query: listChapter,
      variables () {
        return {
          status: 'PUBLISHED',
          sortOn: '_slug',
          descending: false
        }
      },
      async result () {
        const guestProjectIndex = this.ListChapter.items.findIndex(i => i.isGuestProject === true)
        if (guestProjectIndex !== -1) {
          this.$root.guestProject = this.ListChapter.items.splice(guestProjectIndex, 1)[0]
        }
        this.$root.chapters = this.ListChapter.items

        const lastVisitedChapters = localStorage.getItem('lastVisitedChapters')
        if (lastVisitedChapters) {
          this.$root.lastVisitedChapters = JSON.parse(lastVisitedChapters)
        }

        localStorage.setItem('lastVisitedChapters', JSON.stringify(this.$root.chapters))

        if (!this.$root.platform.prerenderer) {
          anime({
            targets: this,
            round: 10,
            percentage: 99.9,
            easing: 'linear',
            duration: INTRO_TIMEOUT,
            delay: 0,
            complete: () => {
              this.step = 'language'
            }
          })
        } else {
          // we are in a prerenderer, select a language if not present
          if (!this.$route.params.language) {
            this.selectedLanguage = 'en'
            this.$router.replace({
              name: this.$route.name,
              params: {
                language: 'en'
              }
            })
          } else {
            this.selectedLanguage = this.$route.params.language
          }
          this.step = 'explore'
          if (this.$root.allSiteSettings) {
            this.$root.siteSettings = this.$root.allSiteSettings[this.selectedLanguage]
            // skip intros and emit loading complete
            this.$emit('loading:complete')
          }
        }
      }
    }
  },

  methods: {

    onSettingsResult (res) {
      // this.$root.siteSettings = res.data.GetSiteSettings
    },
    async onLanguageSelected (lang) {
      this.selectedLanguage = lang
      this.$root.siteSettings = this.$root.allSiteSettings[this.selectedLanguage]

      if (this.$root.siteSettings.introPageImageArray.length > 0) {
        const i = Math.floor(Math.random() * this.$root.siteSettings.introPageImageArray.length)
        const randomItem = this.$root.siteSettings.introPageImageArray[i]
        this.randomImageCredit = {
          credit: randomItem.homepageCredit,
          image: randomItem.homepageImage.id
        }
      } else {
        this.randomImageCredit = {
          credit: this.$root.siteSettings.introPageImageCredits,
          image: this.$root.siteSettings.introPageImage.id
        }
      }

      if (this.$route.params.language !== lang) {
        this.$router.replace({
          name: this.$route.name,
          params: { language: lang }
        })
      }
      await this.$nextTick()
      if (this.$route.name !== 'Home' && this.$route.name !== 'Chapter' && this.$route.name !== 'Chapters') {
        this.$emit('loading:complete')
      } else {
        this.step = 'explore'
      }
    },

    onExploreClick () {
      this.$emit('loading:complete')
    }
  }
}
</script>

<style scoped lang="scss">

.background-image {
  position: fixed;
  top: 0;
  left: 0;

  .ie11 & {
    z-index: 0;
    position: absolute;
  }
}

.info {
  .ie11 & {
    position: fixed;
    bottom: 0;
    z-index: 2;
  }

  h3 {
    @include breakpoint('sm-and-down') {
      font-size: 20px;
    }
  }
}

.preloader-tab {
  height: 100px;

  @include breakpoint('md-and-up') {
    height: 200px;
  }
}

.credit {
  position: absolute;

  right: 5px;
  bottom: calc(50% + 5px);

  color: rgba(255, 255, 255, 1);
  letter-spacing: 1px;
  font-size: 10px;

  @include breakpoint('md-and-up') {
    right: 35px;
    bottom: 35px;
    font-size: 12px;
  }

  img {
    height: 100%;
    width: auto;
  }
}

.language-btn {
  font-family: Barlow, sans-serif;
  font-size: 20px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: normal;
  text-align: center;
  color: $c-light-grey;

  @include breakpoint('md-and-up') {
    font-size: 25px;
  }
}

.circle-logo {
  position: absolute;

  top: 55%;
  transform: translateY(-50%);

  left: -25px;

  border-radius: 50%;
  background-color: $c-red;
  width: 16px;
  height: 16px;
}

h3 {
  position: relative;
}

h4.underline {
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    display: table;

    width: 100%;

    left: 0;
    bottom: 0;

    height: 1px;
    background-color: $c-text-grey;

    transform: translateY(5px);
  }
}

h5 {
  font-size: 13px;
  line-height: 1.5;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  font-family: Lora;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  color: $c-light-grey;

  @include breakpoint('md-and-up') {
    font-size: 18px;
    line-height: 1.2;
  }
}

p {
  font-size: 9px;
  line-height: 1.25;
  font-family: Lora;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  color: $c-light-grey;

  @include breakpoint('md-and-up') {
    font-size: 14px;
    line-height: 1.2;
  }
}

a {
  text-decoration: underline;
}

.intro-container {
  width: 100%;
}
</style>
